import { useMemo } from 'react'
import { useFetchPublicCatalogObjectivesQuery } from '@/store/api/publicCatalogApi'
import { COLLECTION_NO_VALUE } from '@/store/api/showcasesApi'
import { useAppSelector } from '@/store/hooks'
import { createFiltersSelector } from '@/store/slices/aggregatesSlice'

export const usePublicCatalogObjectives = () => {
  const { data: { data: catalogObjectivesResponse = [], totalCount: totalCatalogObjectives = 0 } = {}, isLoading } =
    useFetchPublicCatalogObjectivesQuery()

  const collectionFilters = useAppSelector((state) => createFiltersSelector(state, 'public').collection)
  const categoryFilters = useAppSelector((state) => createFiltersSelector(state, 'public').category)
  const searchText = useAppSelector((state) => state.aggregates.catalogCoursesSearchText)

  const catalogObjectives = useMemo(() => {
    let filteredObjectives = catalogObjectivesResponse

    // Apply collection filters
    if (collectionFilters.length) {
      const hasNoCollection = collectionFilters.includes(COLLECTION_NO_VALUE)

      filteredObjectives = filteredObjectives.filter(({ showcases = [] }) => {
        const collectionIds = showcases.map(({ id }) => id)

        if (hasNoCollection) {
          return collectionIds.length === 0 || collectionIds.some((id) => collectionFilters.includes(id))
        }

        return collectionIds.some((id) => collectionFilters.includes(id))
      })
    }

    // Apply category filters
    if (Object.values(categoryFilters).flat().length) {
      console.log('Category filters found:', categoryFilters)
      filteredObjectives = filteredObjectives.filter(({ filterAttributes = [], name }) => {
        // Only check categories that have attributes selected
        const result = Object.entries(categoryFilters)
          .filter(([, attributeIds]) => attributeIds.length > 0) // Only check categories with selected attributes
          .every(([categoryId, attributeIds]) => {
            const categoryAttributes = filterAttributes.filter((attr) => attr.category.id === categoryId)

            if (categoryAttributes.length === 0) {
              return false
            }

            return categoryAttributes.some((attr) => attributeIds.includes(attr.id))
          })

        return result
      })
    } else {
      console.log('No category filters found:', categoryFilters)
    }

    return filteredObjectives
  }, [catalogObjectivesResponse, collectionFilters, categoryFilters])

  const searchedCatalogObjectives = useMemo(() => {
    const trimmedSearchText = searchText?.trim().toLowerCase()

    if (!trimmedSearchText) {
      return catalogObjectives
    }

    return catalogObjectives.filter(({ name, code, description }) =>
      [name, code, description].some((field) => field?.toLowerCase().includes(trimmedSearchText)),
    )
  }, [searchText, catalogObjectives])

  const uncategorizedCoursesCount = catalogObjectives.filter((course) => !course.showcases?.length).length
  const hasNoShowCase =
    collectionFilters.includes(COLLECTION_NO_VALUE) ||
    (uncategorizedCoursesCount > 0 && uncategorizedCoursesCount !== catalogObjectives.length)

  return {
    catalogObjectives,
    searchedCatalogObjectives,
    totalCatalogObjectives,
    isLoading,
    hasNoShowCase,
  }
}
